import { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SCREENSHOTS } from './screenshots'

const AUTOPLAY_MS = 6000

export function ScreenshotCarousel() {
  const [active, setActive] = useState(0)
  const [autoplay, setAutoplay] = useState(
    () => !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  )
  const [paused, setPaused] = useState(false)
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined')
  const trackRef = useRef<HTMLDivElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  // While a programmatic scroll glides past the slides in between, their scroll events
  // mustn't flicker the selected tab — only the target counts.
  const targetRef = useRef<number | null>(null)

  useEffect(() => {
    const track = trackRef.current
    if (!track || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      threshold: 0.4,
    })
    observer.observe(track)
    return () => observer.disconnect()
  }, [])

  function scrollToSlide(index: number) {
    const track = trackRef.current
    const slide = track?.children[index] as HTMLElement | undefined
    if (!track || !slide) return
    targetRef.current = index
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth',
    })
    const tab = tabsRef.current?.children[index] as HTMLElement | undefined
    tabsRef.current?.scrollTo({
      left: tab ? tab.offsetLeft - 16 : 0,
      behavior: 'smooth',
    })
  }

  function go(index: number) {
    const next = (index + SCREENSHOTS.length) % SCREENSHOTS.length
    setActive(next)
    scrollToSlide(next)
  }

  function takeOver(index: number) {
    setAutoplay(false)
    go(index)
  }

  useEffect(() => {
    if (!autoplay || paused || !visible) return
    const timer = setTimeout(() => go(active + 1), AUTOPLAY_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, autoplay, paused, visible])

  function onScroll() {
    const track = trackRef.current
    if (!track) return
    const center = track.scrollLeft + track.clientWidth / 2
    let nearest = 0
    let best = Infinity
    Array.from(track.children).forEach((child, i) => {
      const el = child as HTMLElement
      const distance = Math.abs(el.offsetLeft + el.clientWidth / 2 - center)
      if (distance < best) {
        best = distance
        nearest = i
      }
    })
    if (targetRef.current !== null) {
      if (nearest !== targetRef.current) return
      targetRef.current = null
    }
    setActive(nearest)
  }

  const shot = SCREENSHOTS[active]

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div
        ref={tabsRef}
        role="tablist"
        aria-label="App screens"
        className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:mb-6 sm:flex-wrap sm:justify-center sm:px-0"
      >
        {SCREENSHOTS.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            id={`screenshot-tab-${s.id}`}
            aria-selected={i === active}
            aria-controls={`screenshot-${s.id}`}
            onClick={() => takeOver(i)}
            className={cn(
              'shrink-0 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors sm:px-4 sm:text-sm',
              i === active
                ? 'border-[var(--fl-primary)] bg-[var(--fl-primary)] text-[var(--fl-primary-fg)]'
                : 'border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-2)] hover:border-[var(--fl-primary)] hover:text-[var(--fl-primary)]'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Sized off the viewport height so tabs, screenshot and caption fit one screen: the desktop
          shot is 16:10 under a 40px bar with ~210px of tabs and caption around it; the phone card
          (1170×2532, so width = height × 0.462) leaves room for the fixed bottom CTA bar too. */}
      <div className="sm:mx-auto sm:max-w-[calc((100svh_-_250px)*1.6)] sm:overflow-hidden sm:rounded-[18px] sm:border sm:border-[var(--fl-border)] sm:bg-[var(--fl-surface)] sm:shadow-[var(--card-shadow)]">
        <div
          aria-hidden="true"
          className="hidden h-10 items-center gap-2 border-b border-[var(--fl-border)] bg-[var(--fl-surface-subtle)] px-4 sm:flex"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fl-border)]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fl-border)]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--fl-border)]" />
          <span className="mx-auto rounded-md bg-[var(--fl-surface)] px-4 py-1 text-xs text-[var(--fl-text-3)]">
            logmyspends.club/app/{shot.id}
          </span>
        </div>
        <div
          ref={trackRef}
          onScroll={onScroll}
          onPointerDown={() => setAutoplay(false)}
          onWheel={() => setAutoplay(false)}
          className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-[max(17%,calc(50%_-_(100svh_-_290px)*0.231))] [scrollbar-width:none] sm:mx-0 sm:gap-0 sm:px-0"
        >
          {SCREENSHOTS.map((s, i) => (
            <div
              key={s.id}
              id={`screenshot-${s.id}`}
              role="tabpanel"
              aria-labelledby={`screenshot-tab-${s.id}`}
              className="w-full shrink-0 snap-center"
            >
              <picture>
                <source
                  media="(max-width: 639px)"
                  srcSet={`/screenshots/phone/${s.id}.webp`}
                  width={1170}
                  height={2532}
                />
                <img
                  src={`/screenshots/desktop/${s.id}.webp`}
                  width={2400}
                  height={1500}
                  alt={s.alt}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  // The first shot is the hero's largest paint, so fetch it ahead of the scripts.
                  fetchPriority={i === 0 ? 'high' : undefined}
                  decoding="async"
                  draggable={false}
                  className="block h-auto w-full rounded-[26px] border border-[var(--fl-border)] shadow-[var(--card-shadow)] sm:rounded-none sm:border-0 sm:shadow-none"
                />
              </picture>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-start justify-between gap-4 sm:mx-auto sm:mt-6 sm:max-w-[calc((100svh_-_250px)*1.6)]">
        <div aria-live="polite" className="min-h-[3.5rem]">
          <h3 className="text-[15px] font-bold text-[var(--fl-heading)] sm:text-lg">
            {shot.title}
          </h3>
          <p className="mt-0.5 text-[13px] leading-relaxed text-[var(--fl-text-2)] sm:text-[15px]">
            {shot.caption}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="mr-1 hidden text-[13px] tabular-nums text-[var(--fl-text-3)] sm:inline">
            {active + 1} / {SCREENSHOTS.length}
          </span>
          <button
            type="button"
            aria-label="Previous screenshot"
            onClick={() => takeOver(active - 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-2)] transition-colors hover:border-[var(--fl-primary)] hover:text-[var(--fl-primary)]"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Next screenshot"
            onClick={() => takeOver(active + 1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--fl-border)] bg-[var(--fl-surface)] text-[var(--fl-text-2)] transition-colors hover:border-[var(--fl-primary)] hover:text-[var(--fl-primary)]"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}
